import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import Loading from "../common/Loading";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import UserRoles from "../../model/enums/UserRole";
import CHElement from "../../model/CHElement";
import ChElementsListElement from "./ChElementsListElement";
import AlterationForm from "../../model/AlterationForm";
import Material from "../../model/Material";
import SearchCriteria from "../../model/SearchCriteria";
import loadSearchCriteria from "../../utils/LoadSearchCriteria";
import SearchFilterCard from "../search/SearchFilterCard";
import SearchFilterCheckboxNumber from "../search/SearchFilterCheckboxNumber";
import SearchFilterCheckboxNumberGroup from "../search/SearchFilterCheckboxNumberGroup";
import ButtonType from "../common/button/ButtonType";
import SearchFilterCheckboxString from "../search/SearchFilterCheckboxString";
import CHElementScale from "../../model/enums/CHElementScale";
import HeritageCategories from "../../model/enums/HeritageCategories";
import ConservationState from "../../model/enums/ConservationState";
import SearchFilterString from "../../model/SearchFilterString";
import SearchFilterAdministrative from "../search/SearchFilterAdministrative";
import MapModal from "../common/map/MapModal";
import AdministrativeStructure from "../../model/AdministrativeStructure";
import SearchFilterMinMax from "../search/SearchFilterMinMax";
import MapDisplay from "../common/map/MapDisplay";
import ReactPaginate from "react-paginate";
import SearchCriteriaTools from "../../utils/SearchCriteriaTools";
import FormDropDown from "../common/FormDropDown";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";

enum DisplayType {
  LIST = "list",
  GRID = "grid",
  MAP = "map",
}

function ChElementsList() {
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // activities information to be displayed
  const [data, setData] = useState<CHElement[]>([]);
  // manage data loading animation
  const [loading, setLoading] = useState(true);
  // information about alteration forms
  const [alterationForms, setAlterationForms] = useState<AlterationForm[]>([]);
  // information about materials
  const [materials, setMaterials] = useState<Material[]>([]);
  // set the data
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>(
    SearchCriteria.EmptyInstance(),
  );
  // add delay to search launch
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // manage display type
  const [displayType, setDisplayType] = useState<DisplayType>(DisplayType.LIST);
  // flag to display/hide map modal
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  // information that will be displayed on the map
  const [mapData, setMapData] = useState<CHElement[]>([]);
  // store the total page count
  const [paginationInfo, setPaginationInfo] = useState<
    PagedData<CHElement[]> | undefined
  >(undefined);

  // load record data from the server
  useEffect(() => {
    setLoading(true);
    const getAllData = async () => {
      try {
        const alter = await apiClient?.get<PagedData<AlterationForm[]>>({
          url: APIEndPoints.ALTERATION_FORMS.ALL,
        });
        setAlterationForms(alter.items);

        const mat = await apiClient?.get<PagedData<Material[]>>({
          url: APIEndPoints.MATERIALS.ALL,
        });
        setMaterials(mat.items);

        const savedSearchCriteria = loadSearchCriteria();
        setSearchCriteria(savedSearchCriteria);

        apiClient
          ?.get<PagedData<CHElement[]>>({
            url: APIEndPoints.CHELEMENTS.SEARCH(savedSearchCriteria),
            requiresToken: true,
          })
          .then((response) => {
            if (response?.items) {
              setData(response.items);
              setPaginationInfo(response);
            }
          })
          .catch((error) => {
            console.error(`Error fetching CH Elements`, error);
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient]);

  const formInputChange = async (
    inputID: string,
    newValue:
      | number
      | string
      | boolean
      | Set<number>
      | Set<string>
      | undefined
      | AdministrativeStructure,
  ) => {
    const searchCriteriaCopy = JSON.parse(JSON.stringify(searchCriteria));
    if (
      inputID == "alteration_id" ||
      inputID == "material_id" ||
      inputID == "types" ||
      inputID == "category" ||
      inputID == "scale" ||
      inputID == "conservation"
    ) {
      searchCriteriaCopy[inputID] = Array.from(newValue as Set<number>);
    } else if (
      inputID === "country" ||
      inputID === "province" ||
      inputID === "municipality"
    ) {
      searchCriteriaCopy[inputID + "_id"] = (
        newValue as AdministrativeStructure
      )?.geonameid;
      searchCriteriaCopy["local_" + inputID] = newValue;

      // cascading the updates to the other sections
      const cascadeItems = [];
      if (inputID === "country") {
        cascadeItems.push("province");
        cascadeItems.push("municipality");
      }
      if (inputID === "province") {
        cascadeItems.push("municipality");
      }
      cascadeItems.map((item) => {
        searchCriteriaCopy[item] = undefined;
        searchCriteriaCopy["local_" + item] = undefined;
      });
    } else {
      searchCriteriaCopy[inputID] = newValue;
    }
    setSearchCriteria(searchCriteriaCopy as SearchCriteria);
    // save the search criteria on local storage
    localStorage.setItem("search_criteria", JSON.stringify(searchCriteriaCopy));
    // launch a new search with the copy, because the STATE will be updated later
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(
      setTimeout(() => {
        performSearch(searchCriteriaCopy as SearchCriteria);
      }, 500),
    );
  };

  const checkForEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      performSearch(searchCriteria);
    }
  };

  const performSearch = async (searchInfo: SearchCriteria) => {
    const results = await apiClient?.get<PagedData<CHElement[]>>({
      url: APIEndPoints.CHELEMENTS.SEARCH(searchInfo),
      requiresToken: true,
    });
    setData(results.items);
    setPaginationInfo(results);
  };

  const showMap = (data: CHElement) => {
    setMapData([data]);
    setIsMapModalOpen(true);
  };

  // Invoke when user click to request another page.
  const handlePageClick = (info: { selected: number }) => {
    console.log(info);
    // update the page on search criteria
    const searchCriteriaCopy = JSON.parse(JSON.stringify(searchCriteria));
    searchCriteriaCopy["page"] = info.selected + 1;
    setSearchCriteria(searchCriteriaCopy as SearchCriteria);
    // save the search criteria on local storage
    localStorage.setItem("search_criteria", JSON.stringify(searchCriteriaCopy));
    // perform the search
    performSearch(searchCriteriaCopy as SearchCriteria);
  };

  const filterRemove = (filterType: string, filterValue: string | number) => {
    console.log("filterRemove", filterType, filterValue);
    // update the page on search criteria
    const searchCriteriaCopy = JSON.parse(JSON.stringify(searchCriteria));
    if (Array.isArray(searchCriteriaCopy[filterType])) {
      const auxSet = new Set(searchCriteriaCopy[filterType]);
      auxSet.delete(filterValue);
      searchCriteriaCopy[filterType] = Array.from(auxSet);
    } else {
      searchCriteriaCopy[filterType] = undefined;
    }
    setSearchCriteria(searchCriteriaCopy as SearchCriteria);
    // save the search criteria on local storage
    localStorage.setItem("search_criteria", JSON.stringify(searchCriteriaCopy));
    // perform the search
    performSearch(searchCriteriaCopy as SearchCriteria);
  };

  const chScale = Object.entries(CHElementScale).map(([key, value]) => {
    return new SearchFilterString(value.toString(), key.replace(/_/g, " "));
  });

  const chCategory = Object.entries(HeritageCategories).map(([key, value]) => {
    return new SearchFilterString(value.toString(), key.replace(/_/g, " "));
  });

  const chConservation = Object.entries(ConservationState).map(
    ([key, value]) => {
      return new SearchFilterString(value.toString(), key.replace(/_/g, " "));
    },
  );

  const sortOptions = [
    { label: "record name", value: "name" },
    { label: "creation date", value: "created_on" },
  ];

  const orderOptions = [
    { label: "ascending", value: "asc" },
    { label: "desscending", value: "desc" },
  ];

  const resultsPerPageOptions = [
    { label: "10 results per page", value: "10" },
    { label: "20 results per page", value: "20" },
    { label: "50 results per page", value: "50" },
  ];

  const filterTags = SearchCriteriaTools.searchCriteriaToTags(
    searchCriteria,
    materials,
    alterationForms,
    filterRemove,
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="search-page">
      <div
        className={
          displayType === DisplayType.MAP ? "search-left-map-flexible" : "search-left-flexible"
        }
      >
        <h2>Filter results by:</h2>
        <SearchFilterCard title="Administrative unit">
          <SearchFilterAdministrative
            onChange={formInputChange}
            selectedCountry={searchCriteria.local_country}
            selectedProvince={searchCriteria.local_province}
            selectedMunicipality={searchCriteria.local_municipality}
            selectTopCSS="select-search-addedit"
          />
        </SearchFilterCard>

        <SearchFilterCard title="Date">
          <SearchFilterMinMax
            nameMin="year_from"
            labelMin="after year"
            valueMin={searchCriteria.year_from?.toString()}
            nameMax="year_to"
            labelMax="before year"
            valueMax={searchCriteria.year_to?.toString()}
            onChange={formInputChange}
          />
        </SearchFilterCard>

        <SearchFilterCard title="Scale of cultural heritage">
          <SearchFilterCheckboxString
            name="scale"
            onChange={formInputChange}
            data={chScale}
            selected={new Set(searchCriteria.scale)}
          />
        </SearchFilterCard>

        <SearchFilterCard title="Category of cultural heritage">
          <SearchFilterCheckboxString
            name="category"
            onChange={formInputChange}
            data={chCategory}
            selected={new Set(searchCriteria.category)}
          />
        </SearchFilterCard>

        <SearchFilterCard title="State of conservation">
          <SearchFilterCheckboxString
            name="conservation"
            onChange={formInputChange}
            data={chConservation}
            selected={new Set(searchCriteria.conservation)}
          />
        </SearchFilterCard>

        <SearchFilterCard title="Materials">
          <SearchFilterCheckboxNumber
            name="material_id"
            onChange={formInputChange}
            data={materials}
            selected={new Set(searchCriteria.material_id)}
          />
        </SearchFilterCard>

        <SearchFilterCard title="Alteration forms">
          {alterationForms.map((item) => {
            return (
              <SearchFilterCheckboxNumberGroup
                name="alteration_id"
                onChange={formInputChange}
                data={item}
                selected={new Set(searchCriteria.alteration_id)}
              />
            );
          })}
        </SearchFilterCard>
      </div>
      <div
        className={
          displayType === DisplayType.MAP ? "search-right-map" : "search-right"
        }
      >
        <div className="search-controls">
          <div className="search-row">
            <input
              type="text"
              name="search"
              id="search"
              value={searchCriteria.search}
              className="search-keyword"
              placeholder="Search ChemiNova repository"
              onChange={(e) => formInputChange("search", e.target.value)}
              onKeyDown={checkForEnter}
              required
            />
            <Button
              label=""
              type={ButtonType.ICON}
              purpose={ButtonAction.LIST}
              onClick={() => setDisplayType(DisplayType.LIST)}
            />
            <Button
              label=""
              type={ButtonType.ICON}
              purpose={ButtonAction.GRID}
              onClick={() => setDisplayType(DisplayType.GRID)}
            />
            <Button
              label=""
              type={ButtonType.ICON}
              purpose={ButtonAction.MAP}
              onClick={() => setDisplayType(DisplayType.MAP)}
            />
          </div>

          <div className="search-row">
            <div className="flex flex-row">
              <div className="mt-[7px] mr-2">Display </div>
              <FormDropDown
                id="per_page"
                label=""
                value={searchCriteria.per_page}
                options={resultsPerPageOptions}
                onChange={formInputChange}
              />
            </div>
            <div className="flex flex-row ml-2">
              <div className="mt-[7px] mr-2">and order results </div>
              <FormDropDown
                id="order"
                label=""
                value={searchCriteria.order}
                options={orderOptions}
                onChange={formInputChange}
              />
            </div>
            <div className="flex flex-row ml-2">
              <div className="mt-[7px] mr-2"> by </div>
              <FormDropDown
                id="sort"
                label=""
                value={searchCriteria.sort}
                options={sortOptions}
                onChange={formInputChange}
              />
            </div>
          </div>
        </div>

        <div className="search-row mx-32 my-8">
          <div>
            Your search returned <span>{paginationInfo?.total}</span> results.
            {filterTags.length > 0 && " Filters: "}
          </div>
          {filterTags}
        </div>

        {data?.length === 0 && (
          <div className="list-no-records">
            No records found. Change search parameters and try again.
          </div>
        )}

        {displayType === DisplayType.GRID && (
          <div className="details-grid mx-32">
            {data?.length > 0 &&
              data.map((item) => {
                // check EDIT rights
                const canEdit =
                  !EmbeddingToolInfo.isChemiTool() &&
                  (item.id_user === authManager.getUserID() ||
                    authManager.getUserRole() === UserRoles.ADMIN);

                return (
                  <ChElementsListElement
                    data={item}
                    displayRows={false}
                    key={item.id}
                    onShowOnMap={showMap}
                  />
                );
              })}
          </div>
        )}

        {displayType === DisplayType.LIST &&
          data?.length > 0 &&
          data.map((item) => {
            // check EDIT rights
            const canEdit =
              !EmbeddingToolInfo.isChemiTool() &&
              (item.id_user === authManager.getUserID() ||
                authManager.getUserRole() === UserRoles.ADMIN);

            return (
              <ChElementsListElement
                data={item}
                displayRows={displayType === DisplayType.LIST}
                key={item.id}
                onShowOnMap={showMap}
              />
            );
          })}

        {displayType === DisplayType.MAP && (
          <div className="w-full h-full p-4">
            <MapDisplay
              data={data}
              isInputEnabled={false}
              fitToEurope={true}
              className="w-full h-full"
            />
          </div>
        )}

        {displayType !== DisplayType.MAP && paginationInfo && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="next >"
            onPageChange={handlePageClick}
            pageRangeDisplayed={5}
            pageCount={paginationInfo?.pages}
            previousLabel="< previous"
            renderOnZeroPageCount={null}
            containerClassName="pagination"
            activeClassName="pagination-active"
            initialPage={searchCriteria.page ? searchCriteria.page - 1 : 0}
          />
        )}
      </div>
      {isMapModalOpen && (
        <MapModal
          title="Cultural Heritate Assets"
          data={mapData}
          onModalClose={() => setIsMapModalOpen(false)}
        />
      )}
    </div>
  );
}

export default ChElementsList;
